<template>
    <div>
        <div class="input-group mb-3" v-for="(row, key) in rows" :key="key">
            <input type="text"
                   class="form-control flex-grow-0 w-25"
                   :class="{'is-invalid': numberInvalid(row) || noteWithoutNumber(row)}"
                   :placeholder="$t('form.parcelNumbers.placeholder')"
                   v-model="row.number"
                   @blur="normalizeLine(key)">
            <input type="text"
                   class="form-control"
                   :placeholder="$t('form.parcelNumbers.notePlaceholder')"
                   v-model="row.note">
            <button type="button"
                    class="btn btn-outline-secondary"
                    :title="$t('form.buttonRemove')"
                    @click="removeLine(key)">
                <span class="fas fa-times"></span>
            </button>
            <div class="invalid-feedback" v-if="numberInvalid(row)">
                {{ $t('form.errors.invalidParcelNumber') }}
            </div>
            <div class="invalid-feedback" v-else-if="noteWithoutNumber(row)">
                {{ $t('form.errors.missingParcelNumber') }}
            </div>
        </div>

        <button type="button" class="btn btn-outline-primary" @click="addLine()">
            {{ $t('form.parcelNumbers.buttonAdd') }}
        </button>
    </div>
</template>

<script lang="ts">
import {Parcel} from '@/components/estimates/common/controls/values';
import {Control} from '@/composables/Control';
import {defineComponent, PropType, ref, watch} from 'vue';

interface Row {
    number: string;
    note: string;
}

const VALID_PATTERN = /^(?:St\. )?\d{1,5}(?:\/\d{1,4})?$/;
const BUILDING_PARCEL = /^(?:st\.?\s*p\.?\s*č\.?|stp\.?|st\.?)\s*(\d{1,5}(?:\/\d{1,4})?)$/i;
const LAND_PARCEL = /^(\d{1,5}(?:\/\d{1,4})?)$/;

function normalize(raw: string): string {
    const trimmed = raw.trim().replace(/\s+/g, ' ');

    const building = trimmed.match(BUILDING_PARCEL);
    if (building) {
        return `St. ${building[1]}`;
    }

    const land = trimmed.match(LAND_PARCEL);
    if (land) {
        return land[1];
    }

    return trimmed;
}

function isValid(raw: string): boolean {
    return VALID_PATTERN.test(normalize(raw));
}

function parseLegacy(value: string): string[] {
    return value
        .split(/\s*,\s*|\s*;\s*|\s+a\s+/i)
        .map(part => part.trim())
        .filter(part => part !== '');
}

// Values stored before the notes are one string of the numbers, split into rows here when the user edits them
function toRows(value: Parcel[] | string | null): Row[] {
    if (value === null) {
        return [];
    }

    if (typeof value === 'string') {
        return parseLegacy(value).map(number => ({number, note: ''}));
    }

    return value.map(parcel => ({number: parcel.number, note: parcel.note ?? ''}));
}

export default defineComponent({
    props: {
        value: {
            type: [Array, String] as PropType<Parcel[] | string | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const rows = ref<Row[]>([]);

        function getValue(): Parcel[] | null {
            const parcels = rows.value
                .filter(row => row.number.trim() !== '')
                .map(row => ({
                    number: normalize(row.number),
                    note: row.note.trim() !== '' ? row.note.trim() : null,
                }));

            return parcels.length > 0 ? parcels : null;
        }

        function validateCurrentValue(): boolean {
            return rows.value.every(row => row.number.trim() === ''
                ? row.note.trim() === ''
                : isValid(row.number));
        }

        const {updateValue, isInvalid} = Control<Parcel[] | string>(props, emit, {
            getValue,
            validateCurrentValue,
        });

        watch(rows, () => {
            updateValue();
        }, {deep: true});

        // created logic
        rows.value = toRows(props.value);
        if (rows.value.length === 0) {
            rows.value = [{number: '', note: ''}];
        }

        // Validate the initial value right away so a stored invalid value
        // (e.g. legacy free text) is reported as invalid immediately and
        // never silently passes as valid before the first watch flush.
        updateValue();

        function numberInvalid(row: Row): boolean {
            return props.validate && row.number.trim() !== '' && !isValid(row.number);
        }

        function noteWithoutNumber(row: Row): boolean {
            return props.validate && row.number.trim() === '' && row.note.trim() !== '';
        }

        function normalizeLine(key: number): void {
            const row = rows.value[key];
            if (row.number.trim() === '') {
                return;
            }
            // Splitting here lets a pasted "148/2, 286" expand into separate rows,
            // not just normalize the single field; the note stays with the first one.
            const [first, ...others] = parseLegacy(row.number).map(normalize);
            if (first === undefined) {
                return;
            }
            rows.value.splice(
                key,
                1,
                {number: first, note: row.note},
                ...others.map(number => ({number, note: ''})),
            );
        }

        function addLine(): void {
            rows.value.push({number: '', note: ''});
        }

        function removeLine(key: number): void {
            rows.value.splice(key, 1);
            if (rows.value.length === 0) {
                rows.value = [{number: '', note: ''}];
            }
        }

        return {
            rows,
            numberInvalid,
            noteWithoutNumber,
            normalizeLine,
            addLine,
            removeLine,
            isInvalid,
        };
    },
});
</script>
